/**
 * Probot Soumission FANUC — frontend (entry point).
 *
 * Bundlé par Vite en IIFE vers assets/js/probot-soumission.js.
 * L'export de ce module est assigné à window.ProbotSoumApp.
 *
 * Endpoints REST consommés :
 *   GET  /wp-json/probot-soum/v1/places
 *   GET  /wp-json/probot-soum/v1/place-details
 *   GET  /wp-json/probot-soum/v1/airport-lookup
 *   GET  /wp-json/probot-soum/v1/verify-email
 *   GET  /wp-json/probot-soum/v1/flight-price
 *   POST /wp-json/probot-soum/v1/save-contact
 *   POST /wp-json/probot-soum/v1/submit
 */

import { S } from './state.js';
import {
	applyAirportResult,
	applyTransportResult,
} from './airports.js';
import {
	renderSelModels, renderDropdown,
	addModel, addQuickPick, changeModelQty, removeModel,
	selSizeRow, getTotalRobots,
} from './models.js';
import { setupPlacesProxy } from './places.js';
import { setLang, gp, validateP1, nx3_describe, nx4_triage } from './panels.js';
import {
	startOfDay, sameDate, fmtDateLong,
	buildMonthHTML, pickDay, computeDuration, toggleSurcharge,
	renderQCal, shiftCalendar,
} from './calendar.js';
import { bind } from './bindings.js';
import { bindServicePanel } from './p2-service.js';
import { initMeeting, initPopup } from './meeting.js';
import { initInstantCall } from './instant-call.js';

function init() {
	if ( ! document.querySelector( '.probot-soum' ) ) return;
	// v1.1.137 — Filet anti-cache pour ?lang=fr|en. Le correctif PHP
	// (class-shortcode::render_template) résout déjà la langue au rendu ; mais si la
	// page est servie depuis un cache de page généré dans l'AUTRE langue, le HTML +
	// window.ProbotSoum.lang arriveraient faux. Le paramètre d'URL, lui, est toujours
	// à jour : on le relit ici et on l'impose AVANT le premier rendu. On synchronise
	// les DEUX sources de langue : window.ProbotSoum.lang (= Cfg, réf. vive lue par
	// meeting.js + instant-call.js) ET S.lang (état lu par setLang + le flow
	// service/places, et envoyé tel quel dans le payload AJAX save-contact/submit →
	// le lead enregistre donc la bonne langue, sans champ caché).
	try {
		var urlLang = new URLSearchParams( window.location.search ).get( 'lang' );
		if ( urlLang === 'fr' || urlLang === 'en' ) {
			if ( window.ProbotSoum ) { window.ProbotSoum.lang = urlLang; }
			S.lang = urlLang;
			// Aligne aussi data-lang du conteneur (le HTML peut venir d'un cache
			// généré dans l'autre langue).
			var psRoots = document.querySelectorAll( '.probot-soum' );
			for ( var ri = 0; ri < psRoots.length; ri++ ) { psRoots[ ri ].setAttribute( 'data-lang', urlLang ); }
		}
	} catch ( e ) { /* URLSearchParams indispo (très vieux navigateur) → on garde la langue serveur */ }
	setLang( S.lang );
	// v1.1.6 — Hydrate les dates engagées par d'autres devis (intervention +
	// travel) depuis le config serveur. Le calendrier les utilisera pour
	// bloquer / styliser. Si vide ou absent, S.unavailable reste vide.
	var bk = ( window.ProbotSoum && window.ProbotSoum.bookedDates ) || {};
	S.unavailable = new Set( Array.isArray( bk.intervention ) ? bk.intervention : [] );
	S.travelDays  = new Set( Array.isArray( bk.travel )       ? bk.travel       : [] );
	// v1.1.20 — Tracker les fenêtres déjà chargées. Initial = 90 jours depuis
	// aujourd'hui (PHP localize). Le calendrier déclenche fetchBookedDates()
	// quand l'utilisateur scroll vers un mois pas encore couvert.
	S.loadedRanges = [];
	var initRange = window.ProbotSoum && window.ProbotSoum.bookedDatesRange;
	if ( initRange && initRange.from && initRange.to ) {
		S.loadedRanges.push( { from: initRange.from, to: initRange.to } );
	}
	// v1.1.16 — Diagnostic : log dans la console combien de dates sont bloquées
	// par la sync Google Calendar. Permet de vérifier en prod sans accès admin
	// que le pipeline serveur → JS fonctionne.
	if ( window.console && window.console.log ) {
		var totalBlocked = S.unavailable.size + S.travelDays.size;
		console.log( '[ProbotSoum] Booked dates loaded:',
			'intervention=' + S.unavailable.size,
			'travel=' + S.travelDays.size,
			'total=' + totalBlocked,
			S.unavailable.size > 0 ? Array.from( S.unavailable ).sort() : '(no intervention dates)'
		);
	}
	// The service flow now has its own Robot step (Direction A 6-step), so
	// the model list / catalog must render for both quote types.
	renderSelModels();
	// Direction A keeps the catalog always visible (it's not a popup
	// dropdown). Pre-render so the user sees the categorised list as soon
	// as P2 becomes active.
	if ( document.querySelector( '.probot-soum.is-srd #modelDD' ) ) {
		renderDropdown();
	}
	bind();
	bindServicePanel();
	setupPlacesProxy();
	// v1.1.77 — Initialize meeting page if shortcode [probot_meeting] is on the page.
	try {
		initMeeting();
	} catch ( e ) {
		if ( window.console ) console.warn( 'meeting init failed', e );
	}
	// v1.1.79 — Initialize popup listeners (independent of meeting page presence).
	try {
		initPopup();
	} catch ( e ) {
		if ( window.console ) console.warn( 'meeting popup init failed', e );
	}
	// v1.1.90 — Initialize instant-call popup state machine (multi-state modal).
	try {
		initInstantCall();
	} catch ( e ) {
		if ( window.console ) console.warn( 'instant-call init failed', e );
	}
	// v1.1.131 — Pré-remplissage « Refaire ma soumission » (si ?refaire=).
	try {
		applyPrefill();
	} catch ( e ) {
		if ( window.console ) console.warn( 'prefill failed', e );
	}
}

// v1.1.131 — Remplit les coordonnées du client depuis window.ProbotSoum.prefill
// (injecté par le serveur quand ?refaire={uuid} pointe vers un devis existant).
// Le tél reste NON-vérifié → le funnel exige une nouvelle vérification SMS.
// Aucun effet sans prefill → les leads normaux (pubs/organique) jamais touchés.
function applyPrefill() {
	var pf = window.ProbotSoum && window.ProbotSoum.prefill;
	if ( ! pf || typeof pf !== 'object' ) { return; }
	function setVal( id, val ) {
		if ( val == null || val === '' ) { return; }
		var el = document.getElementById( id );
		if ( ! el ) { return; }
		el.value = val;
		el.dispatchEvent( new Event( 'input',  { bubbles: true } ) );
		el.dispatchEvent( new Event( 'change', { bubbles: true } ) );
	}
	setVal( 'inFname', pf.firstName );
	setVal( 'inLname', pf.lastName );
	setVal( 'inEmail', pf.email );
	setVal( 'inCo',    pf.company );
	setVal( 'inTel',   pf.phone );
	if ( window.console && window.console.log ) {
		console.log( '[ProbotSoum] Refaire : coordonnées pré-remplies (tél à re-valider).' );
	}
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}

// Test/dev helper — feed an array of YYYY-M-D strings to mark days
// unavailable. Replaced by the Google Calendar sync when it lands.
export function setUnavailable( list ) {
	S.unavailable = new Set( Array.isArray( list ) ? list : [] );
	if ( document.getElementById( 'qcal' ) ) {
		// Re-render the calendar so the new flags take effect.
		import( './calendar.js' ).then( function ( m ) { m.renderQCal(); } );
	}
}

// Export minimal pour debug console + tests unitaires (window.ProbotSoumApp).
export {
	S,
	gp,
	setLang,
	// P1
	validateP1,
	// Service flow split (6 steps)
	nx3_describe,
	nx4_triage,
	applyAirportResult,
	applyTransportResult,
	// P2
	addModel,
	addQuickPick,
	changeModelQty,
	removeModel,
	selSizeRow,
	getTotalRobots,
	// P3
	startOfDay,
	sameDate,
	fmtDateLong,
	buildMonthHTML,
	pickDay,
	computeDuration,
	toggleSurcharge,
	renderQCal,
	shiftCalendar,
};
